import getCategoryFromSortKey from "../helpers/getCategoryFromSortKey";
import getChoiceNumericalValue from "../helpers/getChoiceNumericalValue";
import { evaluate } from "../helpers/securedMathjs";
import { QuestionChoiceType, QuestionType } from "../schemas";
import { MathScopes } from "../types/genericTypes";
import {
  CalculatedAnswer,
  CategorizedFootprint,
  NewAnswer,
} from "../types/questionnaireTypes";

export type AnswersBuilder = {
  buildAnswers: (answers: NewAnswer[]) => {
    categorizedFootprint: CategorizedFootprint;
    calculatedAnswers: CalculatedAnswer[];
    fullMathScope: Record<string, number>;
  };
};

const buildFormulaVariable = (question: QuestionType): string => {
  return `QUESTION_${question.id}_FOOTPRINT`;
};

const calculateFormula = (
  question: QuestionType,
  fullMathScope: MathScopes,
): number => {
  const result =
    question.formula !== "0" ? evaluate(question.formula, fullMathScope) : 0;
  if (typeof result !== "number") {
    const msg = `Formula ${question.formula} of question ${question.questionText} returned non-numeric result: ${result}`;
    const scopeDump = Object.entries(fullMathScope)
      .map(([key, value]) => {
        const type = Array.isArray(value)
          ? "Array"
          : value && typeof value === "object"
            ? ((value as any).constructor?.name ?? "object")
            : typeof value;

        return `${key} -> type=${type} value=${JSON.stringify(value)}`;
      })
      .join("\n");
    console.log(`${msg}\nFull math scope:\n${scopeDump}`);
    throw new Error(msg);
  }
  if (Number.isNaN(result)) {
    console.log(
      `WARNING! Formula ${question.formula} of question ${question.questionText} resolved as NaN`,
    );
  }
  return result;
};

const extractVariablesFromAnswer = (
  question: QuestionType,
  choiceObject: QuestionChoiceType,
  fullMathScope: Record<string, number>,
): Record<string, number> => {
  // For all other types we want to calculate actual numbers and include them to the full math scope
  const answerVariables: Record<string, number> = {};

  const calculatedChoiceValue = getChoiceNumericalValue(
    choiceObject.choiceValue,
    fullMathScope,
  );

  // TODO: We should not mutate the fullMathScope object here. Fix in another PR.
  fullMathScope[question.variableName] = calculatedChoiceValue;
  answerVariables[question.variableName] = calculatedChoiceValue;

  if (question.relatedVariableName && choiceObject.relatedVariableValue) {
    const calculatedChoiceValue = getChoiceNumericalValue(
      choiceObject.relatedVariableValue,
      fullMathScope,
    );
    fullMathScope[question.relatedVariableName] = calculatedChoiceValue;
    answerVariables[question.relatedVariableName] = calculatedChoiceValue;
  }
  answerVariables[buildFormulaVariable(question)] = calculateFormula(
    question,
    fullMathScope,
  );

  return answerVariables;
};

export const createAnswersBuilder = (
  questions: QuestionType[],
  constants: Record<string, number>,
): AnswersBuilder => {
  const questionsById: Record<string, QuestionType> = questions.reduce(
    (aggregator: Record<string, QuestionType>, question) => {
      aggregator[question.id] = question;

      return aggregator;
    },
    {},
  );

  return {
    buildAnswers: (answers: NewAnswer[]) => {
      const { calculatedAnswers, fullMathScope } = answers.reduce(
        ({ calculatedAnswers, fullMathScope }, answer) => {
          // Find original data
          const question = questionsById[answer.questionId];

          if (!question || !question.choices) {
            throw new Error(
              `Question ${question?.id || "NOID"} has no choices to select or question not found`,
            );
          }

          const category = getCategoryFromSortKey(question.sortKey);

          if (!category) {
            return { calculatedAnswers, fullMathScope };
          }

          const choiceObject: QuestionChoiceType | undefined =
            answer.choiceText === null
              ? {
                  choiceText: null,
                  choiceValue: null,
                  choiceTranslationKey: "",
                }
              : question.choices.find(
                  (choice) => choice.choiceText === answer.choiceText,
                );

          if (!choiceObject) {
            throw new Error(
              `Question ${question.id} has missing requested choice`,
            );
          }

          const answerVariables = extractVariablesFromAnswer(
            question,
            choiceObject,
            fullMathScope,
          );

          calculatedAnswers.push({
            questionId: question.id,
            questionText: question.questionText,
            choiceText: choiceObject.choiceText,
            choiceValue: choiceObject.choiceValue,
            category,
            label: question.label,
            footprint: calculateFormula(question, fullMathScope),
            sortKey: question.sortKey,
            variables: answerVariables,
          });

          return { calculatedAnswers, fullMathScope };
        },
        {
          calculatedAnswers: [] as CalculatedAnswer[],
          fullMathScope: { ...constants },
        },
      );

      return {
        categorizedFootprint: calculatedAnswers.reduce(
          (aggregate, calculatedAnswer) => {
            if (calculatedAnswer.category === "demographic") {
              return aggregate;
            }

            aggregate[calculatedAnswer.category] += calculatedAnswer.footprint;

            return aggregate;
          },
          {
            transport: 0,
            purchases: 0,
            housing: 0,
            food: 0,
          },
        ),
        calculatedAnswers,
        fullMathScope,
      };
    },
  };
};
